/**
 * The Property section's address — `docs/new-request.md` §7.1, §7.2,
 * Boards 4, 5 and 14.
 *
 * One box, two groups under it: **Your properties** (live properties in the
 * person's branches whose street matches — `find.ts`'s property query) and
 * **From Google** (the existing Places lookup, through our server). Choosing
 * one of yours is §5.4; choosing Google's fills the street, the town, the
 * state, the ZIP and the coordinates and asks the server about the address
 * (`POST /requests/property-check`): is it already a property, which zone,
 * which branch.
 *
 * **Typed by hand** — *Can't find it? Type the address in yourself* — the
 * box keeps what was typed, says Google could not confirm it, and centres a
 * map on the town with a pin to drag; *Keep it as typed and use the pin I
 * placed* is ticked once the pin moves (§7.1, Board 5 B).
 *
 * ## The session token
 *
 * Google bills a run of suggestions and the details call that ends it once,
 * when they share a token. The token is made **when the box gets focus** and
 * ended by the details call — the same contract `AddressAutocomplete` keeps,
 * with the token made a keystroke earlier so the very first suggestion is in
 * the run.
 */
import {
  Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react'
import { uuidv7 } from 'uuidv7'
import { normalizeRegion, type PropertyCheck } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { geocode } from '../lib/map-provider.js'
import { Avatar } from '../components/primitives.js'
import { TextInput } from '../components/form/index.js'
import { IconPin } from '../frame/icons.js'
import { requestsApi, type FoundPlace } from './api.js'
import { EMPTY_ADDRESS, type SheetState, type TypedAddress, type TypedProperty } from './model.js'
import { useRequestSheet } from './sheet-context.js'
import { Highlight, PlaceMark } from './search-box.js'
import { MissingHint } from './section-frame.js'
import { PinMap, type PinPoint } from './pin-map.js'

// ---------------------------------------------------------------------------
// The property being typed
// ---------------------------------------------------------------------------

/** A new property, empty but for its defaults: Residential, the name auto (§7.3, §7.4). */
export function freshProperty(address: TypedAddress = EMPTY_ADDRESS): TypedProperty {
  return {
    address, type: 'Residential', name: '', nameIsAuto: true, zoneId: null, orgUnitId: null,
    check: null, anotherUnit: false,
  }
}

/** Change the typed property, making one if the section held none. */
export function changeProperty(
  state: SheetState, change: (property: TypedProperty) => TypedProperty,
): SheetState {
  const current = state.property.kind === 'new' ? state.property.property : freshProperty()
  return { ...state, property: { kind: 'new', property: change(current) } }
}

/** *412 Orchard Ridge Dr, Mechanicsburg, PA 17050*. */
export function addressLine(address: TypedAddress, short = false): string {
  const street = [address.line1, address.line2].map((part) => part.trim()).filter(Boolean).join(' ')
  if (!address.city) return street
  if (short) return `${street}, ${address.city}`
  return `${street}, ${address.city}, ${[address.state, address.postalCode].filter(Boolean).join(' ')}`
}

/** Settled by hand: the town was given, Google did not confirm it. */
export const typedByHand = (address: TypedAddress): boolean =>
  !address.verified && address.city.trim() !== ''

/**
 * Board 5 A's band is up: the address is already a property, and nobody has
 * said it is another unit — or the unit they typed is one too.
 */
export function existingShown(property: TypedProperty): PropertyCheck['existing'] {
  const existing = property.check?.existing ?? null
  if (!existing) return null
  if (property.anotherUnit && !property.address.line2.trim()) return null
  return existing
}

/**
 * *7 Harvest Ridge Ln, Mechanicsburg, PA 17050* → its four parts, or null
 * when the town, the state or the ZIP is missing. The state goes through
 * `normalizeRegion`, the one reader of free text (`docs/address-state-field.md`).
 */
export function parseTypedAddress(text: string): {
  line1: string; city: string; state: string; postalCode: string
} | null {
  const parts = text.split(',').map((part) => part.trim()).filter(Boolean)
    .filter((part) => !/^(usa|us|united states)$/i.test(part))
  if (parts.length < 2) return null
  const line1 = parts[0]!
  const rest = parts.slice(1)
  let city: string
  let tail: string
  if (rest.length >= 2) {
    city = rest[0]!
    tail = rest.slice(1).join(' ')
  } else {
    const match = /^(.+?)\s+([A-Za-z][A-Za-z .]*?)\s+(\d{5}(?:-\d{4})?)$/.exec(rest[0]!)
    if (!match) return null
    city = match[1]!
    tail = `${match[2]} ${match[3]}`
  }
  const end = /^(.+?)\s+(\d{5}(?:-\d{4})?)$/.exec(tail)
  if (!end) return null
  const state = normalizeRegion(end[1]!)
  if (!state) return null
  return { line1, city, state, postalCode: end[2]! }
}

const sameAddress = (a: TypedAddress, b: TypedAddress): boolean =>
  a.line1 === b.line1 && a.line2 === b.line2 && a.city === b.city && a.state === b.state
  && a.postalCode === b.postalCode && a.latitude === b.latitude && a.longitude === b.longitude

/**
 * Ask the server about an address, and fold the answer in (§7.2, §7.5).
 *
 * The zone and the branch follow the answer **unless somebody picked them**:
 * a value that is still the last answer's, or empty, is the sheet's to
 * change; anything else was chosen by hand and stays.
 */
export function usePropertyCheck(): (address: TypedAddress) => void {
  const api = useRequestSheet()
  const update = api.update
  const asked = useRef(0)
  return useCallback((address: TypedAddress) => {
    if (!address.line1.trim() || !address.city.trim()) return
    const ticket = ++asked.current
    requestsApi.check({
      line1: address.line1, line2: address.line2 || null, city: address.city,
      state: address.state, postalCode: address.postalCode,
      latitude: address.latitude, longitude: address.longitude,
    }).then((answer) => {
      if (ticket !== asked.current) return
      update((state) => {
        if (state.property.kind !== 'new') return state
        const property = state.property.property
        if (!sameAddress(property.address, address)) return state
        const zoneIsOurs = property.zoneId === null || property.zoneId === property.check?.zone?.id
        const branchIsOurs = property.orgUnitId === null
          || property.orgUnitId === property.check?.branch?.id
        return {
          ...state,
          property: { kind: 'new', property: {
            ...property,
            check: answer,
            zoneId: zoneIsOurs ? answer.zone?.id ?? null : property.zoneId,
            orgUnitId: branchIsOurs ? answer.branch?.id ?? null : property.orgUnitId,
          } },
        }
      })
    }).catch(() => {})
  }, [update])
}

// ---------------------------------------------------------------------------
// Google, through our server
// ---------------------------------------------------------------------------

interface Suggestion { id: string; name: string; sub: string | null }

interface ChosenAddress {
  line1: string; city: string; state: string; postalCode: string
  latitude: number | null; longitude: number | null
}

/** Whether this deployment has a Places key — asked once per page, as the form kit does. */
let placesConfigured: Promise<boolean> | null = null
const placesAvailable = (): Promise<boolean> => {
  placesConfigured ??= request<{ configured: boolean }>('/places/status')
    .then((answer) => answer.configured)
    .catch(() => false)
  return placesConfigured
}

/** Board 4 shows three. */
const GOOGLE_ROWS = 3

type Row =
  | { kind: 'mine'; key: string; place: FoundPlace & { contacts: { id: string; firstName: string; lastName: string; role?: string }[] } }
  | { kind: 'mine-person'; key: string; placeId: string; person: { id: string; firstName: string; lastName: string; role?: string } }
  | { kind: 'google'; key: string; item: Suggestion }

// ---------------------------------------------------------------------------
// The box
// ---------------------------------------------------------------------------

export function AddressBox() {
  const api = useRequestSheet()
  const { phone, update } = api
  const property = api.state.property.kind === 'new' ? api.state.property.property : null
  const address = property?.address ?? EMPTY_ADDRESS
  const canAdd = api.setup.canAddProperties
  const check = usePropertyCheck()

  const input = useRef<HTMLInputElement>(null)
  const session = useRef<string | null>(null)
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const [active, setActive] = useState(0)
  const [mine, setMine] = useState<{ text: string; items: Row[] } | null>(null)
  const [google, setGoogle] = useState<Suggestion[]>([])
  const [problem, setProblem] = useState(false)
  /** Where an address typed by hand puts its pin first: the town. */
  const [centre, setCentre] = useState<PinPoint | null>(null)

  const byHand = typedByHand(address)
  const text = byHand ? addressLine(address) : address.line1
  const words = useMemo(() => text.trim().split(/\s+/).filter((word) => word.length > 1), [text])

  /* Every keystroke is a new address: whatever was confirmed or checked is gone. */
  const type = (value: string) => {
    setProblem(false)
    setCentre(null)
    update((state) => changeProperty(state, (current) => ({
      ...current,
      address: { ...EMPTY_ADDRESS, line1: value, line2: current.address.line2,
        needsUnit: current.address.needsUnit },
      check: null,
    })))
    setOpen(true)
  }

  /* *A new property on …* in the search: the text is already in line1 — focus and offer. */
  useEffect(() => {
    if (api.focus?.field !== 'address') return
    session.current ??= uuidv7()
    setOpen(true)
    input.current?.focus()
  }, [api.focus])

  /* Yours, 150 ms after the last key — the search's own query, its properties group. */
  useEffect(() => {
    const wanted = text.trim()
    if (!open || byHand || address.verified || wanted.length < 2) { setMine(null); return }
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      requestsApi.find(wanted, 6, controller.signal)
        .then((found) => {
          const rows: Row[] = []
          for (const place of found.properties.items) {
            rows.push({ kind: 'mine', key: `p:${place.id}`, place })
            for (const person of place.contacts) {
              rows.push({ kind: 'mine-person', key: `p:${place.id}:${person.id}`, placeId: place.id, person })
            }
          }
          setMine({ text: wanted, items: rows })
          setActive(0)
        })
        .catch(() => {})
    }, 150)
    return () => { window.clearTimeout(timer); controller.abort() }
  }, [text, open, byHand, address.verified])

  /* Google's, a little later, and only with a key and somebody allowed to add a property. */
  useEffect(() => {
    const wanted = text.trim()
    if (!open || !canAdd || byHand || address.verified || wanted.length < 3) { setGoogle([]); return }
    let live = true
    const timer = window.setTimeout(() => {
      void placesAvailable().then((ok) => {
        if (!ok || !live) return
        session.current ??= uuidv7()
        return request<{ items: Suggestion[] }>(
          `/places/suggest?q=${encodeURIComponent(wanted)}&session=${session.current}`)
          .then((answer) => { if (live) setGoogle(answer.items.slice(0, GOOGLE_ROWS)) })
      }).catch(() => { if (live) setGoogle([]) })
    }, 250)
    return () => { live = false; window.clearTimeout(timer) }
  }, [text, open, canAdd, byHand, address.verified])

  const rows: Row[] = [
    ...(mine?.items ?? []),
    ...google.map((item): Row => ({ kind: 'google', key: `g:${item.id}`, item })),
  ]

  const close = () => { setOpen(false); setActive(0) }

  async function takeGoogle(item: Suggestion) {
    const token = session.current ?? uuidv7()
    /* The run ends with the details call, whether or not it answers. */
    session.current = null
    close()
    const found = await request<{ address: ChosenAddress | null }>(
      `/places/${encodeURIComponent(item.id)}?session=${token}`,
    ).catch(() => ({ address: null }))
    if (!found.address || found.address.latitude === null || found.address.longitude === null) {
      type(item.name)
      return
    }
    const chosen = found.address
    settle({
      line1: chosen.line1 || item.name, city: chosen.city, state: chosen.state,
      postalCode: chosen.postalCode, latitude: chosen.latitude, longitude: chosen.longitude,
      placeId: item.id, verified: true, pinKept: false,
    })
  }

  /**
   * Write these parts of the address and ask the server about the result.
   * The address asked about is worked out here, from this render's state,
   * rather than inside the updater — an updater runs when React gets to it,
   * which is after this function has returned.
   */
  function settle(parts: Partial<TypedAddress>) {
    const next: TypedAddress = { ...address, ...parts }
    update((state) => changeProperty(state, (current) => ({
      ...current, address: { ...current.address, ...parts }, check: null,
    })))
    check(next)
  }

  function takeRow(row: Row) {
    close()
    if (row.kind === 'google') { void takeGoogle(row.item); return }
    if (row.kind === 'mine') api.chooseProperty(row.place.id, { inSection: true })
    else api.chooseProperty(row.placeId, { contactId: row.person.id, inSection: true })
  }

  /** *Type the address in yourself* (§7.1). */
  async function keepTyped() {
    close()
    const parsed = parseTypedAddress(text)
    if (!parsed) { setProblem(true); return }
    setProblem(false)
    settle({ ...parsed, latitude: null, longitude: null, placeId: null, verified: false, pinKept: false })
    const hits = await geocode(`${parsed.city}, ${parsed.state}`).catch(() => [])
    const town = hits[0]
    if (town) setCentre({ lat: town.lat, lng: town.lng })
  }

  /** The pin, dropped: these are the property's coordinates now, and the box is ticked. */
  function placePin(point: PinPoint) {
    settle({ latitude: point.lat, longitude: point.lng, pinKept: true })
  }

  /** *Change*: back to typing what was there, suggestions open. */
  function change() {
    update((state) => changeProperty(state, (current) => ({
      ...current,
      address: { ...EMPTY_ADDRESS, line1: current.address.line1, line2: current.address.line2,
        needsUnit: current.address.needsUnit },
      check: null,
    })))
    session.current = uuidv7()
    setOpen(true)
    window.setTimeout(() => input.current?.focus(), 0)
  }

  const label = <FieldLabel required>Address</FieldLabel>
  const unit = property?.anotherUnit ? <UnitField property={property} /> : null

  const settledGoogle = address.verified && address.latitude !== null && address.longitude !== null
  const missing = api.hintFor('address')
  const existing = property ? existingShown(property) : null

  /* Confirmed: the line, *found on the map* and **Change** (desktop); the line on a phone. */
  if (settledGoogle) {
    return (
      <div className="min-w-0">
        {label}
        {phone
          ? (
            <button type="button" onClick={change}
              className="flex h-11 w-full items-center gap-2 rounded-field border border-line bg-surface px-3 text-left">
              <span className="inline-flex text-accent"><IconPin size={16} /></span>
              <span className="min-w-0 flex-1 truncate text-[15px] text-text">{addressLine(address, true)}</span>
            </button>
          )
          : (
            <div className="flex h-[38px] items-center gap-[9px] rounded-control border border-line bg-surface px-3">
              <span className="inline-flex text-accent"><IconPin size={15} /></span>
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-text">
                {addressLine(address)}
              </span>
              <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11.5px] font-bold text-accent-dark">
                <CheckCircle />found on the map
              </span>
              <button type="button" onClick={change}
                className="h-[26px] rounded-button px-1.5 text-[12px] font-semibold text-accent hover:underline">
                Change
              </button>
            </div>
          )}
        <MissingHint text={missing} />
        {unit}
        {!existing && (
          <>
            <div className={phone ? 'h-2' : 'h-2.5'} />
            <PinMap
              point={{ lat: address.latitude!, lng: address.longitude! }}
              label={phone ? null : address.line1}
              height={phone ? 110 : 150}
            />
          </>
        )}
      </div>
    )
  }

  const pinAt: PinPoint | null = address.latitude !== null && address.longitude !== null
    ? { lat: address.latitude, lng: address.longitude }
    : centre

  return (
    <div className="min-w-0">
      {label}
      <div
        className={cn(
          'flex items-center border bg-surface',
          phone ? 'h-11 gap-2 rounded-field px-3' : 'h-[38px] gap-[9px] rounded-control px-3',
          focused ? 'border-accent shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'
            : missing ? 'border-danger' : 'border-line',
        )}
      >
        <span className={cn('inline-flex',
          focused || (text && !byHand) ? 'text-accent' : 'text-text-faint')}>
          <IconPin size={phone ? 16 : 15} />
        </span>
        <span className="relative flex min-w-0 flex-1">
        {/*
          * Typed by hand and left, the address reads as a line of text over
          * the box (Board 5 B) — and is one, so it can be read and found —
          * with the input under it taking the click.
          */}
        {byHand && !focused && (
          <span
            aria-hidden
            onMouseDown={(event) => { event.preventDefault(); input.current?.focus() }}
            className={cn('absolute inset-0 flex items-center truncate bg-surface text-text',
              phone ? 'text-[15px]' : 'text-[13.5px]')}
          >
            {text}
          </span>
        )}
        <input
          ref={input}
          data-request-field="address"
          value={text}
          aria-label="Address"
          role="combobox"
          aria-expanded={open && rows.length > 0}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder={phone ? 'Start typing an address' : 'Start typing — your properties first, then Google'}
          onFocus={() => { setFocused(true); session.current ??= uuidv7(); if (text.trim()) setOpen(true) }}
          onBlur={() => { setFocused(false); close() }}
          onChange={(event) => type(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' && rows.length > 0) {
              event.preventDefault()
              setOpen(true)
              setActive((index) => Math.min(index + 1, rows.length - 1))
            } else if (event.key === 'ArrowUp' && rows.length > 0) {
              event.preventDefault()
              setActive((index) => Math.max(index - 1, 0))
            } else if (event.key === 'Enter' && open) {
              event.preventDefault()
              const row = rows[active]
              if (row) takeRow(row)
              else if (canAdd && text.trim()) void keepTyped()
            } else if (event.key === 'Escape' && open) {
              event.stopPropagation()
              close()
            }
          }}
          className={cn('min-w-0 flex-1 bg-transparent text-text outline-none focus-visible:shadow-none placeholder:text-text-faint',
            phone ? 'text-[15px]' : 'text-[13.5px]')}
        />
        </span>
      </div>
      {byHand && !open && (
        <span className="mt-1 block text-[11px] leading-[1.4] text-warn">
          Google couldn’t confirm this address — new houses often aren’t on the map yet
        </span>
      )}
      {problem && (
        <span className="mt-1 block text-[11px] leading-[1.4] text-warn">
          Add the town, state and ZIP after the street, with commas — 7 Harvest Ridge Ln, Mechanicsburg, PA 17050
        </span>
      )}
      <MissingHint text={missing} />
      {unit}

      {open && text.trim().length >= 2 && (mine || google.length > 0) && (
        <div
          role="listbox"
          aria-label="Addresses"
          onMouseDown={(event) => event.preventDefault()}
          className="mt-1.5 rounded-[12px] border border-line bg-surface pb-1.5 pt-1 shadow-[0_14px_34px_rgba(20,20,10,.12)]"
        >
          <GroupLabel>Your properties</GroupLabel>
          {mine && mine.items.length === 0 && (
            <div className="px-[18px] pb-1.5 pt-0.5 text-[12px] text-text-muted">None at {mine.text}…</div>
          )}
          {rows.map((row, index) => (
            <Fragment key={row.key}>
              {row.kind === 'google' && rows.findIndex((one) => one.kind === 'google') === index && (
                <GroupLabel>From Google</GroupLabel>
              )}
              <OptionRow active={index === active} indent={row.kind === 'mine-person'}
                onHover={() => setActive(index)} onChoose={() => takeRow(row)}>
                {rowContent(row, words)}
              </OptionRow>
            </Fragment>
          ))}
          {canAdd && (
            /* One line where it fits; whole phrases on the next where it doesn't — never a column of halves (Board 14). */
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 border-t border-line-faint px-3.5 pb-0.5 pt-2 text-[11.5px] text-text-faint">
              <span className="whitespace-nowrap">Can’t find it?</span>
              <button type="button" onClick={() => void keepTyped()}
                className="whitespace-nowrap font-bold text-accent hover:underline">
                Type the address in yourself
              </button>
              <span className="flex-1" />
              {google.length > 0 && <span className="whitespace-nowrap italic">powered by Google</span>}
            </div>
          )}
        </div>
      )}

      {byHand && pinAt && !existing && (
        <>
          <div className={phone ? 'h-2' : 'h-2.5'} />
          <PinMap point={pinAt} label={phone ? null : 'drag to the house'} height={phone ? 110 : 120}
            draggable onMove={placePin} />
          <div className="h-2" />
          <button
            type="button"
            role="checkbox"
            aria-checked={address.pinKept}
            onClick={() => {
              if (address.pinKept) {
                update((state) => changeProperty(state, (current) => ({
                  ...current, address: { ...current.address, pinKept: false },
                })))
              } else placePin(pinAt)
            }}
            className="flex items-center gap-2 text-left"
          >
            <span className={cn('inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px]',
              address.pinKept ? 'bg-accent text-surface' : 'border-[1.5px] border-line-strong bg-surface')}>
              {address.pinKept && <Tick />}
            </span>
            <span className={phone ? 'text-[14px]' : 'text-[12.5px]'}>Keep it as typed and use the pin I placed</span>
          </button>
        </>
      )}
    </div>
  )
}

export function FieldLabel({ children, required }: { children: ReactNode; required?: boolean }) {
  return (
    <div className="mb-[5px] flex items-center gap-1 text-[11.5px] font-medium text-text-muted">
      {children}
      {required && <i aria-hidden className="inline-block h-[5px] w-[5px] rounded-full bg-accent" />}
    </div>
  )
}

export const UNIT_HINT = 'Another unit or lot asks for it (Unit 2, Lot 14) — the address with the unit is then a different address.'

/** *Unit or lot*, required once somebody says the address is another one (§7.2). */
function UnitField({ property }: { property: TypedProperty }) {
  const api = useRequestSheet()
  const check = usePropertyCheck()
  const box = useRef<HTMLSpanElement>(null)
  const line2 = property.address.line2
  const settled = property.address.verified || typedByHand(property.address)

  useEffect(() => {
    if (api.focus?.field !== 'unit') return
    box.current?.querySelector('input')?.focus()
  }, [api.focus])

  /* A different unit is a different address: ask again, once typing pauses. */
  useEffect(() => {
    if (!settled || !line2.trim()) return
    const timer = window.setTimeout(() => {
      if (api.state.property.kind === 'new') check(api.state.property.property.address)
    }, 400)
    return () => window.clearTimeout(timer)
    /* The address is read when the timer fires; the unit is what changed. */
  }, [line2, settled])

  return (
    <div className="mt-2.5">
      <FieldLabel required>Unit or lot</FieldLabel>
      <span ref={box} className="block">
        <TextInput
          value={line2}
          data-request-field="unit"
          size={api.phone ? 'touch' : 'default'}
          invalid={Boolean(api.hintFor('unit'))}
          placeholder="Unit 2, Lot 14"
          onChange={(event) => {
            const value = event.target.value
            /* The match was for the address without this unit; ask again (below). */
            api.update((state) => changeProperty(state, (current) => ({
              ...current,
              address: { ...current.address, line2: value },
              check: current.check ? { ...current.check, existing: null } : null,
            })))
          }}
        />
      </span>
      <MissingHint text={api.hintFor('unit')} />
      <div className="mt-1 text-[11px] leading-[1.4] text-text-faint">{UNIT_HINT}</div>
    </div>
  )
}

function rowContent(row: Row, words: string[]): ReactNode {
  if (row.kind === 'google') {
    return (
      <>
        <span className="inline-flex text-text-muted"><IconPin size={15} /></span>
        <span className="flex min-w-0 flex-col">
          <b className="truncate text-[13px] font-semibold text-text">{row.item.name}</b>
          {row.item.sub && <span className="truncate text-[11.5px] text-text-muted">{row.item.sub}</span>}
        </span>
      </>
    )
  }
  if (row.kind === 'mine-person') {
    const name = `${row.person.firstName} ${row.person.lastName}`
    return (
      <>
        <Avatar name={name} size={30} />
        <span className="flex min-w-0 flex-1 flex-col">
          <b className="truncate text-[13px] font-semibold text-text">{name}</b>
          {row.person.role && <span className="truncate text-[11.5px] text-text-muted">{row.person.role}</span>}
        </span>
      </>
    )
  }
  const place = row.place
  return (
    <>
      <PlaceMark seed={place.id} colour={place.zoneColour} size={30} />
      <span className="flex min-w-0 flex-1 flex-col">
        <b className="truncate text-[13px] font-semibold text-text"><Highlight text={place.name} words={words} /></b>
        <span className="truncate text-[11.5px] text-text-muted">
          <Highlight text={`${place.line1}, ${place.city}`} words={words} />
          {` · ${place.jobs} job${place.jobs === 1 ? '' : 's'}`}
        </span>
      </span>
    </>
  )
}

function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center px-[18px] pb-1 pt-2 text-label uppercase text-text-faint">{children}</div>
  )
}

function OptionRow({ active, indent, onHover, onChoose, children }: {
  active: boolean
  indent?: boolean
  onHover: () => void
  onChoose: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onMouseEnter={onHover}
      onClick={onChoose}
      className={cn('mx-1.5 flex w-[calc(100%-12px)] items-center gap-2.5 rounded-[9px] px-3 py-2 text-left leading-tight',
        indent && 'ml-[42px] w-[calc(100%-48px)]',
        active && 'bg-green-band-bg')}
    >
      {children}
    </button>
  )
}

/** Board 5's circled tick beside *found on the map*. */
function CheckCircle() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l3 3 5-6" />
    </svg>
  )
}

function Tick() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  )
}
